import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'

function token(): string | null {
  return localStorage.getItem('mf_access')
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`
  return headers
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // non-JSON error response
  }
  const errBody =
    (body as { error?: { code: string; message: string; details?: Record<string, unknown> } } | null)
      ?.error ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, errBody)
}

export interface OrderLocationBrief {
  id: string
  name: string
  address: string | null
}

export interface OrderListItem {
  id: string
  orderNumber: string
  status: string
  type: string
  source: string
  guestName: string
  total: number
  currency: string
  location: OrderLocationBrief | null
  createdAt: string
}

export interface OrderItemModifierSnapshot {
  name: string
  price: number
  quantity: number
}

export interface OrderItemDetail {
  id: string
  productName: string
  variantName: string
  quantity: number
  unitPrice: number
  totalPrice: number
  modifiers: OrderItemModifierSnapshot[]
}

export interface OrderStatusLogEntry {
  fromStatus: string | null
  toStatus: string
  changedBy: string | null
  reason: string | null
  createdAt: string
}

export interface OrderDetail {
  id: string
  orderNumber: string
  status: string
  type: string
  source: string
  guestName: string
  guestPhone: string
  guestEmail: string
  deliveryAddress: string | null
  deliveryCoords: { lat: number; lng: number } | null
  comment: string
  itemsTotal: number
  deliveryFee: number
  discountTotal: number
  total: number
  currency: string
  locationId: string | null
  location: OrderLocationBrief | null
  createdAt: string
  updatedAt: string
  items: OrderItemDetail[]
  statusLog: OrderStatusLogEntry[]
}

export interface OrderListMeta {
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface OrderListResponse {
  data: OrderListItem[]
  meta: OrderListMeta
}

export interface OrderListQuery {
  page?: number
  pageSize?: number
  status?: string
  locationId?: string
  type?: string
  dateFrom?: string
  dateTo?: string
}


export type OrderBoardColumnKey = 'new' | 'inProgress' | 'preparing' | 'ready' | 'completedToday'

export interface OrderBoardCard {
  id: string
  orderNumber: string
  status: string
  type: string
  source: string
  guestName: string
  total: number
  currency: string
  location: OrderLocationBrief | null
  createdAt: string
  fulfilledAt: string | null
}

export type OrderBoardColumns = Record<OrderBoardColumnKey, OrderBoardCard[]>

export interface OrderBoardResponse {
  columns: OrderBoardColumns
  timezone: string
  localDate: string
  generatedAt: string
}

export interface DashboardOrderItem {
  id: string
  orderNumber: string
  status: string
  type: string
  guestName: string
  total: number
  currency: string
  createdAt: string
}

export interface DashboardPopularDish {
  name: string
  quantity: number
}

export interface DashboardResponse {
  timezone: string
  localDate: string
  recentOrders: DashboardOrderItem[]
  popularDishes: DashboardPopularDish[]
}

export interface UpdateOrderStatusData {
  status: string
  reason?: string
}

export async function getOrders(organizationId: string, query: OrderListQuery = {}): Promise<OrderListResponse> {
  const params = new URLSearchParams()
  if (query.page) params.set('page', String(query.page))
  if (query.pageSize) params.set('pageSize', String(query.pageSize))
  if (query.status) params.set('status', query.status)
  if (query.locationId) params.set('locationId', query.locationId)
  if (query.type) params.set('type', query.type)
  if (query.dateFrom) params.set('dateFrom', query.dateFrom)
  if (query.dateTo) params.set('dateTo', query.dateTo)
  const qs = params.toString()
  const res = await fetch(
    `${API_BASE}/organizations/${organizationId}/orders${qs ? `?${qs}` : ''}`,
    { headers: authHeaders() },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to load orders')
  return res.json() as Promise<OrderListResponse>
}

export async function getOrder(organizationId: string, orderId: string): Promise<OrderDetail> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/orders/${orderId}`, {
    headers: authHeaders(),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to load order')
  const body = await res.json()
  return body.data
}

export async function updateOrderStatus(
  organizationId: string,
  orderId: string,
  data: UpdateOrderStatusData,
): Promise<OrderDetail> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/orders/${orderId}/status`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update order status')
  const body = await res.json()
  return body.data
}
export async function getOrderBoard(
  organizationId: string,
  query: { locationId?: string; type?: string } = {},
): Promise<OrderBoardResponse> {
  const params = new URLSearchParams()
  if (query.locationId) params.set('locationId', query.locationId)
  if (query.type) params.set('type', query.type)
  const qs = params.toString()
  const res = await fetch(
    `${API_BASE}/organizations/${organizationId}/orders/board${qs ? `?${qs}` : ''}`,
    { headers: authHeaders() },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to load order board')
  const body = await res.json()
  return body.data
}

export async function getDashboard(organizationId: string): Promise<DashboardResponse> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/orders/dashboard`, {
    headers: authHeaders(),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to load dashboard')
  const body = await res.json()
  return body.data
}

export interface OrderPositionData {
  orderedIds: string[]
}

export async function setOrderPosition(
  organizationId: string,
  orderId: string,
  position: { beforeOrderId?: string; afterOrderId?: string },
): Promise<OrderPositionData> {
  const res = await fetch(
    `${API_BASE}/organizations/${organizationId}/orders/${orderId}/position`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(position),
    },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to update order position')
  const body = await res.json()
  return body.data
}
